/**
 * Every API call the SPA makes, in one place.
 *
 * Components never build a URL: they call a function from here, so a route change is a
 * one-line edit rather than a hunt through JSX.
 */
import { apiDownload, apiRequest, type Paginated as PaginatedEnvelope } from "@/lib/api";

import type {
  CatalogPlace,
  StartSearchBody,
  PriceGatePayload,
  ProposalDetail,
  ProposalSummary,
  ConfirmedPrice,
  PriceWatch,
  ProposalVariantDraft,
  ProposalVariantPatch,
  ProposalVariantRow,
  PublicProposal,
  StoredTourOffer,
  TourSearchSession,
} from "./search-types";

import type { SettingItem, SettingsDocument } from "./settings-registry-types";
import type { ReportPayload } from "./report-types";

import type {
  AviaPage,
  Contract,
  ContractTemplate,
  PaymentLink,
  PaymentLinkDraft,
  PublicContractDocument,
  PublicPaymentLink,
  SignatureConfirmed,
  SigningCodeSent,
  Booking,
  Branch,
  BranchComparisonRow,
  Task,
  CashAccount,
  CashAccountKind,
  Currency,
  Client,
  Dashboard,
  Deal,
  DealFinanceSummary,
  DealNote,
  DebtorsReport,
  DestinationMarginRow,
  Discipline,
  DisciplineCategory,
  DynamicsRow,
  ExpectedPayment,
  Expense,
  ExpenseCategory,
  ExpenseDraft,
  FinanceOverview,
  InvitedMembership,
  KanbanColumn,
  LostReason,
  Membership,
  MembershipInvite,
  ManagerPerformanceRow,
  AuditEvent,
  Notification,
  TenantProfile,
  UnreadCount,
  NotificationPreferenceChange,
  NotificationPreferenceGrid,
  Payment,
  PaymentDraft,
  PermissionRow,
  PipelineStage,
  Rate,
  TenantSettings,
  Traveler,
  Vendor,
  VendorMarginRow,
  DealStageEvent,
  TravelerDocument,
  TravelerPassport,
  PassportGrant,
  BookingCreateBody,
  DuplicateFinding,
} from "./types";

type Query = Readonly<Record<string, string | number | undefined>>;

// ---------------------------------------------------------------- core
export const coreApi = {
  branches: (): Promise<Branch[]> => apiRequest<Branch[]>("/api/branches/"),

  notifications: (query?: Query): Promise<PaginatedEnvelope<Notification>> =>
    apiRequest<PaginatedEnvelope<Notification>>("/api/notifications/", { query }),

  unreadCount: (): Promise<UnreadCount> =>
    apiRequest<UnreadCount>("/api/notifications/unread-count/"),

  markNotificationRead: (id: number): Promise<Notification> =>
    apiRequest<Notification>(`/api/notifications/${id}/read/`, { method: "POST" }),

  markAllNotificationsRead: (): Promise<{ unread: number }> =>
    apiRequest<{ unread: number }>("/api/notifications/read-all/", { method: "POST" }),

  notificationPreferences: (): Promise<NotificationPreferenceGrid> =>
    apiRequest<NotificationPreferenceGrid>("/api/notifications/preferences/"),

  saveNotificationPreferences: (
    changes: readonly NotificationPreferenceChange[],
  ): Promise<NotificationPreferenceGrid> =>
    apiRequest<NotificationPreferenceGrid>("/api/notifications/preferences/", {
      method: "PUT",
      body: changes,
    }),

  auditEvents: (query?: Query): Promise<PaginatedEnvelope<AuditEvent>> =>
    apiRequest<PaginatedEnvelope<AuditEvent>>("/api/audit/", { query }),
};

// ---------------------------------------------------------------- settings
export const settingsApi = {
  tenantSettings: (): Promise<TenantSettings> =>
    apiRequest<TenantSettings>("/api/tenant-settings/"),

  updateTenantSettings: (patch: Partial<TenantSettings>): Promise<TenantSettings> =>
    apiRequest<TenantSettings>("/api/tenant-settings/", { method: "PATCH", body: patch }),

  /** `Agentlik profili` (TZ §17.14): name, address and phone. The owner alone may change them. */
  tenantProfile: (): Promise<TenantProfile> => apiRequest<TenantProfile>("/api/tenant-profile/"),

  updateTenantProfile: (patch: Partial<TenantProfile>): Promise<TenantProfile> =>
    apiRequest<TenantProfile>("/api/tenant-profile/", { method: "PATCH", body: patch }),

  /** Uploads the agency logo (PNG, JPEG or WebP, at most 2 MB); the server checks the bytes. */
  uploadTenantLogo: (file: File): Promise<TenantProfile> => {
    const body = new FormData();
    body.append("logo", file);
    return apiRequest<TenantProfile>("/api/tenant-profile/logo/", { method: "POST", body });
  },

  /** The settings registry, grouped by section (core/settings_registry.py). */
  settingsRegistry: (): Promise<SettingsDocument> => apiRequest<SettingsDocument>("/api/settings/"),

  /** One channel account's channel-scope settings. */
  channelSettings: (accountId: number): Promise<{ items: SettingItem[] }> =>
    apiRequest<{ items: SettingItem[] }>(`/api/messaging/accounts/${accountId}/settings/`),

  /** Batch change of one channel's settings; a refusal for any key writes none of them. */
  updateChannelSettings: (
    accountId: number,
    values: Record<string, unknown>,
  ): Promise<{ items: SettingItem[] }> =>
    apiRequest<{ items: SettingItem[] }>(`/api/messaging/accounts/${accountId}/settings/`, {
      method: "PATCH",
      body: { values },
    }),

  /** Validated as one batch on the server: every value lands, or none does. */
  updateSettingsRegistry: (values: Record<string, unknown>): Promise<SettingsDocument> =>
    apiRequest<SettingsDocument>("/api/settings/", { method: "PATCH", body: { values } }),

  /** Generated from core/permissions.py; the labels live in the SPA dictionary. */
  permissionMatrix: (): Promise<PermissionRow[]> =>
    apiRequest<PermissionRow[]>("/api/permission-matrix/"),

  branches: (): Promise<Branch[]> => apiRequest<Branch[]>("/api/branches/"),

  createBranch: (body: { name: string; address?: string; phone?: string }): Promise<Branch> =>
    apiRequest<Branch>("/api/branches/", { method: "POST", body }),

  updateBranch: (id: number, patch: Partial<Branch>): Promise<Branch> =>
    apiRequest<Branch>(`/api/branches/${String(id)}/`, { method: "PATCH", body: patch }),

  memberships: (query?: Query): Promise<PaginatedEnvelope<Membership>> =>
    apiRequest<PaginatedEnvelope<Membership>>("/api/memberships/", { query }),

  inviteMember: (body: MembershipInvite): Promise<InvitedMembership> =>
    apiRequest<InvitedMembership>("/api/memberships/", { method: "POST", body }),

  updateMembership: (id: number, patch: Partial<Membership>): Promise<Membership> =>
    apiRequest<Membership>(`/api/memberships/${String(id)}/`, { method: "PATCH", body: patch }),

  createCashAccount: (body: {
    name: string;
    currency: Currency;
    kind: CashAccountKind;
    opening_balance?: string;
  }): Promise<CashAccount> =>
    apiRequest<CashAccount>("/api/finance/cash-accounts/", { method: "POST", body }),

  updateCashAccount: (id: number, patch: Partial<CashAccount>): Promise<CashAccount> =>
    apiRequest<CashAccount>(`/api/finance/cash-accounts/${String(id)}/`, {
      method: "PATCH",
      body: patch,
    }),

  createStage: (body: { name: string; order?: number }): Promise<PipelineStage> =>
    apiRequest<PipelineStage>("/api/pipeline-stages/", { method: "POST", body }),

  updateStage: (id: number, patch: Partial<PipelineStage>): Promise<PipelineStage> =>
    apiRequest<PipelineStage>(`/api/pipeline-stages/${String(id)}/`, {
      method: "PATCH",
      body: patch,
    }),

  /** The whole board order in one request -- a partial list is refused. */
  reorderStages: (order: number[]): Promise<PipelineStage[]> =>
    apiRequest<PipelineStage[]>("/api/pipeline-stages/reorder/", {
      method: "POST",
      body: { order },
    }),
};

// ---------------------------------------------------------------- dashboard
export const dashboardApi = {
  /** `Bosh sahifa`. Shaped by the caller's role; see the Dashboard type. */
  dashboard: (): Promise<Dashboard> => apiRequest<Dashboard>("/api/dashboard/"),
};

// ---------------------------------------------------------------- crm
export const crmApi = {
  stages: (): Promise<PipelineStage[]> => apiRequest<PipelineStage[]>("/api/pipeline-stages/"),

  clients: (query?: Query): Promise<PaginatedEnvelope<Client>> =>
    apiRequest<PaginatedEnvelope<Client>>("/api/clients/", { query }),

  client: (id: number): Promise<Client> => apiRequest<Client>(`/api/clients/${id}/`),

  /** §3.7: who else might be this client, with the level and the reason for each. */
  clientDuplicates: (id: number): Promise<DuplicateFinding[]> =>
    apiRequest<DuplicateFinding[]>(`/api/clients/${String(id)}/duplicates/`),

  /** Absorb `duplicate` into `primary` (§4.6). The primary survives; `Ajratish` undoes it. */
  mergeClient: (primaryId: number, duplicateId: number): Promise<unknown> =>
    apiRequest<unknown>(`/api/clients/${String(primaryId)}/merge/`, {
      method: "POST",
      body: { duplicate: duplicateId },
    }),

  /** A traveller's files this caller may read: a passport scan only for the passport roles. */
  travelerDocuments: (travelerId: number): Promise<PaginatedEnvelope<TravelerDocument>> =>
    apiRequest<PaginatedEnvelope<TravelerDocument>>(
      `/api/travelers/${String(travelerId)}/documents/`,
      { query: { page_size: 100 } },
    ),

  /**
   * One stored file, decrypted by the server (TZ §3.5). A passport scan has no storage link, so
   * it is fetched with the session and opened from a local object URL.
   */
  travelerDocumentFile: (travelerId: number, documentId: number): Promise<Blob> =>
    apiDownload(`/api/travelers/${String(travelerId)}/documents/${String(documentId)}/file/`),

  /**
   * The passport number and birth date in full. Every call is written to the audit journal on
   * the server; the screen calls this only when the person presses `Ko'rsatish`.
   */
  travelerPassport: (travelerId: number): Promise<TravelerPassport> =>
    apiRequest<TravelerPassport>(`/api/travelers/${String(travelerId)}/passport/`),

  /** The owner's list of who may read this traveller's passport (TZ §1.4). */
  passportGrants: (travelerId: number): Promise<PaginatedEnvelope<PassportGrant>> =>
    apiRequest<PaginatedEnvelope<PassportGrant>>(
      `/api/travelers/${String(travelerId)}/passport-grants/`,
      { query: { page_size: 100 } },
    ),

  /** Give one agency member passport access to this traveller. Owner only. */
  grantPassport: (travelerId: number, membershipId: number): Promise<PassportGrant> =>
    apiRequest<PassportGrant>(`/api/travelers/${String(travelerId)}/passport-grants/`, {
      method: "POST",
      body: { membership: membershipId },
    }),

  /** Withdraw a live grant. The row stays, stamped with the time and the owner. Owner only. */
  revokePassportGrant: (travelerId: number, grantId: number): Promise<PassportGrant> =>
    apiRequest<PassportGrant>(
      `/api/travelers/${String(travelerId)}/passport-grants/${String(grantId)}/revoke/`,
      { method: "POST" },
    ),

  /** Every stage move on a deal, newest first (Tarix). */
  dealStageHistory: (dealId: number, query?: Query): Promise<PaginatedEnvelope<DealStageEvent>> =>
    apiRequest<PaginatedEnvelope<DealStageEvent>>(`/api/deals/${String(dealId)}/stage-history/`, {
      query,
    }),

  deals: (query?: Query): Promise<PaginatedEnvelope<Deal>> =>
    apiRequest<PaginatedEnvelope<Deal>>("/api/deals/", { query }),

  deal: (id: number): Promise<Deal> => apiRequest<Deal>(`/api/deals/${id}/`),

  kanban: (query?: Query): Promise<KanbanColumn[]> =>
    apiRequest<KanbanColumn[]>("/api/deals/kanban/", { query }),

  lostReasons: (): Promise<LostReason[]> => apiRequest<LostReason[]>("/api/lost-reasons/"),

  tasks: (query: Query): Promise<PaginatedEnvelope<Task>> =>
    apiRequest<PaginatedEnvelope<Task>>("/api/tasks/", { query }),

  completeTask: (id: number): Promise<Task> =>
    apiRequest<Task>(`/api/tasks/${String(id)}/complete/`, { method: "POST" }),

  cancelTask: (id: number, reason: string): Promise<Task> =>
    apiRequest<Task>(`/api/tasks/${String(id)}/cancel/`, { method: "POST", body: { reason } }),

  postponeTask: (id: number, dueAt: string): Promise<Task> =>
    apiRequest<Task>(`/api/tasks/${String(id)}/postpone/`, {
      method: "POST",
      body: { due_at: dueAt },
    }),

  bookings: (query: Query): Promise<PaginatedEnvelope<Booking>> =>
    apiRequest<PaginatedEnvelope<Booking>>("/api/bookings/", { query }),

  /** `Bron qo'shish`. Money goes as decimal strings; the cost only when the caller may see it. */
  createBooking: (body: BookingCreateBody): Promise<Booking> =>
    apiRequest<Booking>("/api/bookings/", { method: "POST", body }),

  dealNotes: (dealId: number, query?: Query): Promise<PaginatedEnvelope<DealNote>> =>
    apiRequest<PaginatedEnvelope<DealNote>>(`/api/deals/${String(dealId)}/notes/`, { query }),

  addDealNote: (dealId: number, body: string): Promise<DealNote> =>
    apiRequest<DealNote>(`/api/deals/${String(dealId)}/notes/`, { method: "POST", body: { body } }),

  /** What drag-and-drop calls, and what the card's arrow button falls back to.
   *  `lostReason` is required exactly when the target stage is flagged `is_lost`. */
  /** A loss needs both a reason and a comment (CLAUDE.md §4c); any other move sends neither. */
  moveDealStage: (id: number, stage: number, lost?: { reason: number; comment: string }): Promise<Deal> =>
    apiRequest<Deal>(`/api/deals/${String(id)}/move-stage/`, {
      method: "POST",
      body:
        lost === undefined
          ? { stage }
          : { stage, lost_reason: lost.reason, comment: lost.comment.trim() },
    }),

  updateDeal: (id: number, patch: Partial<Deal>): Promise<Deal> =>
    apiRequest<Deal>(`/api/deals/${String(id)}/`, { method: "PATCH", body: patch }),

  travelers: (query?: Query): Promise<PaginatedEnvelope<Traveler>> =>
    apiRequest<PaginatedEnvelope<Traveler>>("/api/travelers/", { query }),
};

// ---------------------------------------------------------------- finance
export const financeApi = {
  overview: (): Promise<FinanceOverview> => apiRequest<FinanceOverview>("/api/finance/overview/"),

  /** The rate for one date — what the payment modal's equivalent line is computed with. */
  rate: (date: string, currency: Currency): Promise<Rate> =>
    apiRequest<Rate>("/api/finance/rate/", { query: { date, currency } }),

  cashAccounts: (): Promise<CashAccount[]> =>
    apiRequest<CashAccount[]>("/api/finance/cash-accounts/"),

  expenseCategories: (): Promise<ExpenseCategory[]> =>
    apiRequest<ExpenseCategory[]>("/api/finance/expense-categories/"),

  vendors: (): Promise<Vendor[]> => apiRequest<Vendor[]>("/api/finance/vendors/"),

  payments: (query?: Query): Promise<PaginatedEnvelope<Payment>> =>
    apiRequest<PaginatedEnvelope<Payment>>("/api/finance/payments/", { query }),

  approvalQueue: (query?: Query): Promise<PaginatedEnvelope<Payment>> =>
    apiRequest<PaginatedEnvelope<Payment>>("/api/finance/payments/approval-queue/", { query }),

  createPayment: (draft: PaymentDraft): Promise<Payment> =>
    apiRequest<Payment>("/api/finance/payments/", { method: "POST", body: draft }),

  approvePayment: (id: number): Promise<Payment> =>
    apiRequest<Payment>(`/api/finance/payments/${id}/approve/`, { method: "POST" }),

  rejectPayment: (id: number, reason: string): Promise<Payment> =>
    apiRequest<Payment>(`/api/finance/payments/${id}/reject/`, {
      method: "POST",
      body: { reason },
    }),

  /** Cancels an approved payment by writing its reversal. Returns the storno entry. */
  stornoPayment: (id: number, reason: string): Promise<Payment> =>
    apiRequest<Payment>(`/api/finance/payments/${id}/storno/`, {
      method: "POST",
      body: { reason },
    }),

  expenses: (query?: Query): Promise<PaginatedEnvelope<Expense>> =>
    apiRequest<PaginatedEnvelope<Expense>>("/api/finance/expenses/", { query }),

  createExpense: (draft: ExpenseDraft): Promise<Expense> =>
    apiRequest<Expense>("/api/finance/expenses/", { method: "POST", body: draft }),

  expectedPayments: (query?: Query): Promise<PaginatedEnvelope<ExpectedPayment>> =>
    apiRequest<PaginatedEnvelope<ExpectedPayment>>("/api/finance/expected-payments/", { query }),

  createExpectedPayment: (draft: {
    deal: number;
    amount: string;
    expected_date: string;
    note?: string;
  }): Promise<ExpectedPayment> =>
    apiRequest<ExpectedPayment>("/api/finance/expected-payments/", {
      method: "POST",
      body: draft,
    }),

  deleteExpectedPayment: (id: number): Promise<null> =>
    apiRequest<null>(`/api/finance/expected-payments/${id}/`, { method: "DELETE" }),

  dealSummary: (dealId: number): Promise<DealFinanceSummary> =>
    apiRequest<DealFinanceSummary>(`/api/finance/deals/${dealId}/summary/`),

  discipline: (clientId: number): Promise<Discipline> =>
    apiRequest<Discipline>(`/api/finance/clients/${clientId}/discipline/`),

  // ---------------------------------------------------------------- reports
  reportDynamics: (query: Query): Promise<DynamicsRow[]> =>
    apiRequest<DynamicsRow[]>("/api/finance/reports/dynamics/", { query }),

  reportMarginByDestination: (query: Query): Promise<DestinationMarginRow[]> =>
    apiRequest<DestinationMarginRow[]>("/api/finance/reports/margin-by-destination/", {
      query,
    }),

  reportMarginByVendor: (query: Query): Promise<VendorMarginRow[]> =>
    apiRequest<VendorMarginRow[]>("/api/finance/reports/margin-by-vendor/", { query }),

  reportDebtors: (query: Query): Promise<DebtorsReport> =>
    apiRequest<DebtorsReport>("/api/finance/reports/debtors/", { query }),

  reportManagers: (query: Query): Promise<ManagerPerformanceRow[]> =>
    apiRequest<ManagerPerformanceRow[]>("/api/finance/reports/managers/", { query }),

  /** Owner-only; every other role gets a 403. */
  reportBranches: (query: Query): Promise<BranchComparisonRow[]> =>
    apiRequest<BranchComparisonRow[]>("/api/finance/reports/branches/", { query }),

  setDisciplineOverride: (
    clientId: number,
    override: DisciplineCategory | "",
  ): Promise<Discipline> =>
    apiRequest<Discipline>(`/api/finance/clients/${clientId}/discipline/`, {
      method: "PATCH",
      body: { override },
    }),
};

// ---------------------------------------------------------------- tour search
/**
 * These call a third party through our backend, which throttles them on their own
 * scope. Keep the call count down: debounce inputs, and do not refetch on focus.
 */
/**
 * The Hisobotlar endpoints (`/api/reports/`). Each returns a `ReportPayload`; the same
 * URL with `export=xlsx` returns the workbook, which goes through `apiDownload`.
 */
export const reportsApi = {
  payload: (name: string, query: Query): Promise<ReportPayload> =>
    apiRequest<ReportPayload>(`/api/reports/${name}/`, { query }),

  /** The `.xlsx` for a report, as bytes. The query is the one the screen sent. */
  download: (name: string, query: Query): Promise<Blob> => {
    const search = new URLSearchParams({ export: "xlsx" });
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        search.set(key, String(value));
      }
    }
    return apiDownload(`/api/reports/${name}/?${search.toString()}`);
  },
};

export const toursApi = {
  /** Aviabiletlar: a price source, not a supplier login. Errors arrive as ApiError. */
  aviaSearch: (query: Query): Promise<AviaPage> =>
    apiRequest<AviaPage>("/api/tours/avia/search/", { query }),
};

// ---------------------------------------------------------------- contracts
export interface ContractTemplateDraft {
  name: string;
  body: string;
  is_active: boolean;
}

/**
 * `Shartnoma`. Every write is a named action because each is a step of §12.1 with its own
 * refusals. The list has no deal filter on the server, so callers narrow it by `deal`.
 */
export const contractsApi = {
  templates: (): Promise<PaginatedEnvelope<ContractTemplate>> =>
    apiRequest<PaginatedEnvelope<ContractTemplate>>("/api/contract-templates/", {
      query: { page_size: 200 },
    }),

  /** Owner only: the wording is the agency's own document (contracts app, D455). */
  createTemplate: (body: ContractTemplateDraft): Promise<ContractTemplate> =>
    apiRequest<ContractTemplate>("/api/contract-templates/", { method: "POST", body }),

  updateTemplate: (id: number, patch: Partial<ContractTemplateDraft>): Promise<ContractTemplate> =>
    apiRequest<ContractTemplate>(`/api/contract-templates/${String(id)}/`, {
      method: "PATCH",
      body: patch,
    }),

  list: (): Promise<PaginatedEnvelope<Contract>> =>
    apiRequest<PaginatedEnvelope<Contract>>("/api/contracts/", { query: { page_size: 200 } }),

  create: (body: { deal: number; template: number }): Promise<Contract> =>
    apiRequest<Contract>("/api/contracts/", { method: "POST", body }),

  updateBody: (id: number, body: string): Promise<Contract> =>
    apiRequest<Contract>(`/api/contracts/${String(id)}/body/`, { method: "PATCH", body: { body } }),

  send: (id: number): Promise<Contract> =>
    apiRequest<Contract>(`/api/contracts/${String(id)}/send/`, { method: "POST" }),

  void: (id: number, reason: string): Promise<Contract> =>
    apiRequest<Contract>(`/api/contracts/${String(id)}/void/`, { method: "POST", body: { reason } }),

  /** The draft's watermarked PDF, or the stored signed copy. */
  pdf: (id: number): Promise<Blob> => apiDownload(`/api/contracts/${String(id)}/pdf/`),
};

// ---------------------------------------------------------------- payment links
export const paymentLinksApi = {
  list: (query?: Query): Promise<PaginatedEnvelope<PaymentLink>> =>
    apiRequest<PaginatedEnvelope<PaymentLink>>("/api/finance/payment-links/", { query }),

  create: (draft: PaymentLinkDraft): Promise<PaymentLink> =>
    apiRequest<PaymentLink>("/api/finance/payment-links/", { method: "POST", body: draft }),

  cancel: (id: number, reason: string): Promise<PaymentLink> =>
    apiRequest<PaymentLink>(`/api/finance/payment-links/${String(id)}/cancel/`, {
      method: "POST",
      body: { reason },
    }),
};

// ---------------------------------------------------------------- public (no account)
/**
 * The signing and payment pages a client opens from a link. Every call is `anonymous`:
 * no bearer token is attached and a 401 never triggers a refresh.
 */
export const publicApi = {
  signingDocument: (token: string): Promise<PublicContractDocument> =>
    apiRequest<PublicContractDocument>(`/api/sign/${encodeURIComponent(token)}/`, {
      anonymous: true,
    }),

  requestSigningCode: (token: string): Promise<SigningCodeSent> =>
    apiRequest<SigningCodeSent>(`/api/sign/${encodeURIComponent(token)}/code/`, {
      method: "POST",
      anonymous: true,
    }),

  confirmSignature: (
    token: string,
    body: { code: string; signature_png: string },
  ): Promise<SignatureConfirmed> =>
    apiRequest<SignatureConfirmed>(`/api/sign/${encodeURIComponent(token)}/confirm/`, {
      method: "POST",
      body,
      anonymous: true,
    }),

  /** The frozen text before signing; the signed copy after it. */
  signingPdf: (token: string): Promise<Blob> =>
    apiDownload(`/api/sign/${encodeURIComponent(token)}/pdf/`, { anonymous: true }),

  payLink: (token: string): Promise<PublicPaymentLink> =>
    apiRequest<PublicPaymentLink>(`/api/pay/${encodeURIComponent(token)}/`, { anonymous: true }),
};

// ---------------------------------------------------------------- tour search, fan-out

/**
 * The whole catalog in one page, for the search form's pickers. The default page is 25 rows,
 * and the production catalog holds more than that, so without the size a destination past
 * the first page (Turkiya sorts by country code) was not offered at all.
 */
export const catalogPlaceApi = {
  destinations: (): Promise<PaginatedEnvelope<CatalogPlace>> =>
    apiRequest<PaginatedEnvelope<CatalogPlace>>("/api/catalog/destinations/", {
      query: { page_size: 200 },
    }),

  departureCities: (): Promise<PaginatedEnvelope<CatalogPlace>> =>
    apiRequest<PaginatedEnvelope<CatalogPlace>>("/api/catalog/departure-cities/", {
      query: { page_size: 200 },
    }),
};

/** One destination the agency sells, as `Sozlamalar → Yo'nalishlar` lists it (TZ §10.3). */
export interface TenantDestinationRow {
  id: number;
  destination: number;
  catalog_name: string;
  country_code: string;
  display_name: string;
  label: string;
  is_priority: boolean;
  sort_order: number;
  is_active: boolean;
}

/** The agency's own destination selection. Owner and branch manager change it; nothing is deleted. */
export const tenantDestinationApi = {
  list: (page: number): Promise<PaginatedEnvelope<TenantDestinationRow>> =>
    apiRequest<PaginatedEnvelope<TenantDestinationRow>>("/api/catalog/tenant-destinations/", {
      query: { page },
    }),

  add: (destination: number): Promise<TenantDestinationRow> =>
    apiRequest<TenantDestinationRow>("/api/catalog/tenant-destinations/", {
      method: "POST",
      body: { destination, is_active: true },
    }),

  setActive: (id: number, isActive: boolean): Promise<TenantDestinationRow> =>
    apiRequest<TenantDestinationRow>(`/api/catalog/tenant-destinations/${id}/`, {
      method: "PATCH",
      body: { is_active: isActive },
    }),
};



/** Watch a stored offer for a target price. Refused while the agency's feature is off. */
export const priceWatchApi = {
  watch: (offerId: number, targetPrice: string): Promise<PriceWatch> =>
    apiRequest<PriceWatch>(`/api/tours/offers/${String(offerId)}/watch/`, {
      method: "POST",
      body: { target_price: targetPrice },
    }),

  /** The watches on offers this agency can see, newest first. */
  list: (): Promise<PaginatedEnvelope<PriceWatch>> =>
    apiRequest<PaginatedEnvelope<PriceWatch>>("/api/tours/price-watches/", {
      query: { page_size: 200 },
    }),

  /** Stop watching: the watch stays in the list, marked off, so its history stays readable. */
  stop: (watchId: number): Promise<PriceWatch> =>
    apiRequest<PriceWatch>(`/api/tours/price-watches/${String(watchId)}/stop/`, { method: "POST" }),
};

export const tourSearchApi = {
  list: (): Promise<PaginatedEnvelope<TourSearchSession>> =>
    apiRequest<PaginatedEnvelope<TourSearchSession>>("/api/tours/searches/"),

  start: (body: StartSearchBody): Promise<TourSearchSession> =>
    apiRequest<TourSearchSession>("/api/tours/searches/", { method: "POST", body }),

  session: (id: number): Promise<TourSearchSession> =>
    apiRequest<TourSearchSession>(`/api/tours/searches/${String(id)}/`),

  offers: (id: number, query?: Query): Promise<PaginatedEnvelope<StoredTourOffer>> =>
    apiRequest<PaginatedEnvelope<StoredTourOffer>>(`/api/tours/searches/${String(id)}/offers/`, {
      query,
    }),
};

// ---------------------------------------------------------------- proposals
export const proposalApi = {
  /** A deal's pages, newest first. Only pages on deals the caller may see come back. */
  forDeal: (dealId: number): Promise<PaginatedEnvelope<ProposalSummary>> =>
    apiRequest<PaginatedEnvelope<ProposalSummary>>("/api/proposals/", {
      query: { deal: dealId, page_size: 50 },
    }),

  /** One page with its options, for the editor. */
  detail: (proposalId: number): Promise<ProposalDetail> =>
    apiRequest<ProposalDetail>(`/api/proposals/${String(proposalId)}/`),

  addVariant: (proposalId: number, body: ProposalVariantDraft): Promise<ProposalVariantRow> =>
    apiRequest<ProposalVariantRow>(`/api/proposals/${String(proposalId)}/variants/`, {
      method: "POST",
      body,
    }),

  updateVariant: (
    proposalId: number,
    variantId: number,
    patch: ProposalVariantPatch,
  ): Promise<ProposalVariantRow> =>
    apiRequest<ProposalVariantRow>(
      `/api/proposals/${String(proposalId)}/variants/${String(variantId)}/`,
      { method: "PATCH", body: patch },
    ),

  removeVariant: (proposalId: number, variantId: number): Promise<null> =>
    apiRequest<null>(`/api/proposals/${String(proposalId)}/variants/${String(variantId)}/`, {
      method: "DELETE",
    }),

  /**
   * Freezes a draft and records the send. The server runs the price gate first: a 409 carries
   * `price_changes` (resend with `confirmedPrices`) or `unavailable` (cannot be sent).
   */
  send: (proposalId: number, confirmedPrices?: PriceGatePayload["price_changes"]): Promise<ProposalSummary> =>
    apiRequest<ProposalSummary>(`/api/proposals/${String(proposalId)}/send/`, {
      method: "POST",
      body: confirmedPrices === undefined ? {} : { confirmed_prices: confirmedPrices },
    }),

  /** Takes a sent page down before it expires. The row stays on the record. */
  withdraw: (proposalId: number): Promise<ProposalSummary> =>
    apiRequest<ProposalSummary>(`/api/proposals/${String(proposalId)}/withdraw/`, {
      method: "POST",
    }),

  /** Creates the page for a deal and, unless `send` is false, sends the link. */
  createForDeal: (
    dealId: number,
    body: {
      variants: ProposalVariantDraft[];
      send: boolean;
      /** Prices the operator accepted after the supplier moved them (D170). */
      confirmed_prices?: ConfirmedPrice[];
    },
  ): Promise<ProposalSummary> =>
    apiRequest<ProposalSummary>(`/api/deals/${String(dealId)}/proposals/`, {
      method: "POST",
      body,
    }),

  /** Public, no session. The token is the only credential, so it is never logged here. */
  publicPage: (token: string): Promise<PublicProposal> =>
    apiRequest<PublicProposal>(`/api/p/${encodeURIComponent(token)}/`, { anonymous: true }),

  choose: (token: string, variantId: number): Promise<PublicProposal> =>
    apiRequest<PublicProposal>(
      `/api/p/${encodeURIComponent(token)}/variants/${String(variantId)}/choose/`,
      { method: "POST", anonymous: true },
    ),
};
